import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TextField, Divider, Typography } from '@mui/material'
import ScriptEditorDialog from './ScriptEditorDialog'
import RepositoryScriptsSection from './RepositoryScriptsSection'
import { uploadRatelimitSupported } from '../utils/uploadRatelimit'

type OnFailureMode = 'fail' | 'continue' | 'skip'

interface AdvancedRepositoryOptionsProps {
  repositoryId?: number | null
  mode: 'full' | 'observe'
  /** Borg 2 has no upload limit (2.0.0b22 removed --upload-ratelimit),
   * except behind rclone, which applies it as its bandwidth limit. */
  borgVersion?: 1 | 2
  repositoryPath?: string
  remotePath: string
  preBackupScript: string
  postBackupScript: string
  preHookTimeout: number
  postHookTimeout: number
  hookFailureMode: OnFailureMode
  customFlags: string
  uploadRatelimitMb: string
  onRemotePathChange: (value: string) => void
  onPreBackupScriptChange: (value: string) => void
  onPostBackupScriptChange: (value: string) => void
  onPreHookTimeoutChange: (value: number) => void
  onPostHookTimeoutChange: (value: number) => void
  onHookFailureModeChange: (value: OnFailureMode) => void
  onCustomFlagsChange: (value: string) => void
  onUploadRatelimitMbChange: (value: string) => void
  /** An agent repository runs only scripts its agent publishes. */
  agentRepository?: boolean
}

export default function AdvancedRepositoryOptions({
  repositoryId,
  mode,
  borgVersion = 1,
  repositoryPath,
  remotePath,
  preBackupScript,
  postBackupScript,
  preHookTimeout,
  postHookTimeout,
  hookFailureMode,
  customFlags,
  uploadRatelimitMb,
  onRemotePathChange,
  onPreBackupScriptChange,
  onPostBackupScriptChange,
  onPreHookTimeoutChange,
  onPostHookTimeoutChange,
  onHookFailureModeChange,
  onCustomFlagsChange,
  onUploadRatelimitMbChange,
  agentRepository = false,
}: AdvancedRepositoryOptionsProps) {
  const { t } = useTranslation()
  const [preScriptDialogOpen, setPreScriptDialogOpen] = useState(false)
  const [postScriptDialogOpen, setPostScriptDialogOpen] = useState(false)
  const [hasPreLibraryScripts, setHasPreLibraryScripts] = useState(false)
  const [hasPostLibraryScripts, setHasPostLibraryScripts] = useState(false)
  const uploadLimitSupported = uploadRatelimitSupported(borgVersion, repositoryPath)

  return (
    <>
      <Divider sx={{ mt: 2 }} />
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          mt: 2,
        }}
      >
        {t('advancedRepositoryOptions.title')}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          display: 'block',
          mb: 1.5,
        }}
      >
        {t('advancedRepositoryOptions.subtitle')}
      </Typography>

      {/* Remote Path */}
      <TextField
        label={t('advancedRepositoryOptions.remoteBorgPath')}
        value={remotePath}
        onChange={(e) => onRemotePathChange(e.target.value)}
        placeholder="/usr/local/bin/borg"
        fullWidth
        helperText={t('advancedRepositoryOptions.remoteBorgPathHint')}
      />

      {/* Custom Flags - Only show for full repositories */}
      {mode === 'full' && (
        <>
          <TextField
            label={t('advancedRepositoryOptions.customFlags')}
            value={customFlags}
            onChange={(e) => onCustomFlagsChange(e.target.value)}
            placeholder="--stats --list --filter AME"
            fullWidth
            helperText={t('advancedRepositoryOptions.customFlagsHint')}
          />

          <TextField
            label={t('advancedRepositoryOptions.uploadSpeedLimit')}
            value={uploadLimitSupported ? uploadRatelimitMb : ''}
            onChange={(e) => onUploadRatelimitMbChange(e.target.value)}
            type="number"
            fullWidth
            disabled={!uploadLimitSupported}
            helperText={
              !uploadLimitSupported
                ? t('advancedRepositoryOptions.uploadSpeedLimitBorg2')
                : borgVersion === 2
                  ? t('advancedRepositoryOptions.uploadSpeedLimitRclone')
                  : t('advancedRepositoryOptions.uploadSpeedLimitHint')
            }
            slotProps={{
              htmlInput: { min: 0, step: 0.1 },
            }}
          />
        </>
      )}

      {/* Scripts Section - Grouped by timing (pre-backup / post-backup) */}
      {mode === 'full' && (
        <>
          <Divider sx={{ mt: 3, mb: 1.5 }} />
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              mb: 0.5,
            }}
          >
            {t('advancedRepositoryOptions.scripts')}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              display: 'block',
              mb: 1,
            }}
          >
            {t(
              agentRepository
                ? 'advancedRepositoryOptions.agentScriptsHint'
                : 'advancedRepositoryOptions.scriptsHint'
            )}
          </Typography>

          <RepositoryScriptsSection
            repositoryId={repositoryId}
            preBackupScript={preBackupScript}
            postBackupScript={postBackupScript}
            onPreBackupScriptChange={onPreBackupScriptChange}
            onPostBackupScriptChange={onPostBackupScriptChange}
            onOpenPreScriptDialog={() => setPreScriptDialogOpen(true)}
            onOpenPostScriptDialog={() => setPostScriptDialogOpen(true)}
            hasPreLibraryScripts={hasPreLibraryScripts}
            hasPostLibraryScripts={hasPostLibraryScripts}
            onPreLibraryScriptsChange={setHasPreLibraryScripts}
            onPostLibraryScriptsChange={setHasPostLibraryScripts}
            agentRepository={agentRepository}
          />
        </>
      )}

      {/* Script Editor Dialogs */}
      <ScriptEditorDialog
        open={preScriptDialogOpen}
        onClose={() => setPreScriptDialogOpen(false)}
        title={t('advancedRepositoryOptions.preBackupScript')}
        value={preBackupScript}
        onChange={onPreBackupScriptChange}
        placeholder="#!/bin/bash&#10;echo 'Pre-backup hook started'&#10;wakeonlan AA:BB:CC:DD:EE:FF&#10;sleep 60"
        timeout={preHookTimeout}
        onTimeoutChange={onPreHookTimeoutChange}
        onFailureMode={hookFailureMode}
        onFailureModeChange={onHookFailureModeChange}
        showContinueOnFailure={true}
        repositoryId={repositoryId}
      />

      <ScriptEditorDialog
        open={postScriptDialogOpen}
        onClose={() => setPostScriptDialogOpen(false)}
        title={t('advancedRepositoryOptions.postBackupScript')}
        value={postBackupScript}
        onChange={onPostBackupScriptChange}
        placeholder="#!/bin/bash&#10;echo 'Post-backup hook completed'&#10;ssh nas@192.168.1.100 'sudo poweroff'"
        timeout={postHookTimeout}
        onTimeoutChange={onPostHookTimeoutChange}
        showContinueOnFailure={false}
        repositoryId={repositoryId}
      />
    </>
  )
}
